import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/services/api';
import { Globe, Sun, Moon, ArrowLeft, Eye, EyeOff } from 'lucide-react';

export function LoginPage() {
  const { login } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [forgotInfo, setForgotInfo] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.forgotPassword(email);
      setForgotInfo(data.message || (language === 'es' ? 'Te enviamos un código de uso único a tu email.' : 'We sent a one-time code to your email.'));
      setView('reset');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.resetPassword(email, resetCode, newPassword);
      setView('reset-done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setView('login');
    setError('');
    setPassword('');
    setResetCode('');
    setNewPassword('');
    setForgotInfo('');
  };

  const isLight = theme === 'light';
  const inputBase = `w-full px-5 py-4 rounded-2xl outline-none transition-colors text-base ${
    isLight
      ? 'bg-[#0F0F0F] text-white placeholder-gray-400 border-2 border-[#0F0F0F]'
      : 'bg-white text-[#0F0F0F] placeholder-gray-500 border-2 border-white'
  }`;

  return (
    <div className="min-h-screen-safe bg-background flex flex-col" data-testid="login-page">
      <div
        className="flex items-center justify-end gap-2 px-6 py-4"
        style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
      >
        <button
          onClick={toggleTheme}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-foreground"
          aria-label="Toggle theme"
          data-testid="login-theme-toggle"
        >
          {isLight ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
        </button>
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-full hover:bg-muted transition-colors text-foreground"
          aria-label="Toggle language"
          data-testid="login-lang-toggle"
        >
          <Globe className="w-3 h-3" />
          {language === 'es' ? 'EN' : 'ES'}
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-10 -mt-8">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-10">
            <img
              src="/logo512.png"
              alt="WannaColive"
              className="w-32 h-32 brand-logo-adaptive"
            />
            <p className="font-heading font-bold text-2xl text-foreground mt-3 tracking-tight">
              Wanna<span className="font-medium text-muted-foreground">Colive</span>
            </p>
          </div>

          {view === 'login' && (
            <>
              <div className="text-center mb-8">
                <h1 className="font-heading font-bold text-2xl text-foreground mb-2">
                  {language === 'es' ? 'Iniciar Sesión' : 'Sign in'}
                </h1>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {language === 'es' ? (
                    <>Ingresa tu correo electrónico<br />para ingresar en esta aplicación</>
                  ) : (
                    <>Enter your email<br />to access this application</>
                  )}
                </p>
              </div>

              {error && (
                <div
                  className="mb-4 p-3 bg-destructive/15 border-2 border-destructive rounded-2xl text-sm text-destructive text-center"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputBase}
                  placeholder={language === 'es' ? 'correoelectrónico@dominio.com' : 'email@domain.com'}
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                />
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputBase + ' pr-12'}
                    placeholder={language === 'es' ? 'contraseña' : 'password'}
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? (language === 'es' ? 'Ocultar contraseña' : 'Hide password') : (language === 'es' ? 'Mostrar contraseña' : 'Show password')}
                    data-testid="login-password-toggle"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className={`w-full py-4 rounded-2xl font-semibold text-base transition-all disabled:cursor-not-allowed ${
                    isLight
                      ? 'bg-[#9A9A9A] text-white hover:bg-[#7A7A7A] disabled:bg-[#9A9A9A]'
                      : 'bg-[#9A9A9A] text-white hover:bg-[#7A7A7A] disabled:bg-[#5A5A5A]'
                  } ${(!email || !password) ? 'opacity-90' : ''}`}
                  data-testid="login-submit-button"
                >
                  {loading ? (language === 'es' ? 'Ingresando…' : 'Signing in…') : (language === 'es' ? 'Continuar' : 'Continue')}
                </button>
              </form>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => { setView('forgot'); setError(''); }}
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                  data-testid="forgot-password-link"
                >
                  {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
                </button>
              </div>

              <p className="mt-6 text-center text-xs text-muted-foreground leading-relaxed px-2">
                {language === 'es' ? (
                  <>Al hacer clic en continuar, aceptas nuestros <span className="text-foreground">Términos de servicio</span> y <span className="text-foreground">Política de privacidad</span></>
                ) : (
                  <>By clicking continue, you accept our <span className="text-foreground">Terms of Service</span> and <span className="text-foreground">Privacy Policy</span></>
                )}
              </p>
            </>
          )}

          {view === 'forgot' && (
            <>
              <div className="text-center mb-8">
                <h1 className="font-heading font-bold text-2xl text-foreground mb-2">
                  {language === 'es' ? 'Recuperar contraseña' : 'Reset password'}
                </h1>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {language === 'es'
                    ? 'Ingresá tu email y te enviamos un código de uso único para reestablecer tu contraseña.'
                    : 'Enter your email and we will send you a one-time code to reset your password.'}
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-destructive/15 border-2 border-destructive rounded-2xl text-sm text-destructive text-center" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleForgot} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputBase}
                  placeholder={language === 'es' ? 'correoelectrónico@dominio.com' : 'email@domain.com'}
                  required
                  autoComplete="email"
                  data-testid="forgot-email-input"
                />
                <button
                  type="submit"
                  disabled={loading || !email}
                  className={`w-full py-4 rounded-2xl font-semibold text-base transition-all disabled:cursor-not-allowed ${
                    isLight
                      ? 'bg-[#0F0F0F] text-white hover:bg-[#2A2A2A] disabled:bg-[#5A5A5A]'
                      : 'bg-white text-[#0F0F0F] hover:bg-gray-200 disabled:bg-[#5A5A5A]'
                  }`}
                  data-testid="forgot-submit-button"
                >
                  {loading
                    ? (language === 'es' ? 'Procesando…' : 'Processing…')
                    : (language === 'es' ? 'Enviar código' : 'Send code')}
                </button>
              </form>

              <p className="mt-4 text-center text-xs text-muted-foreground leading-relaxed px-2" data-testid="forgot-spam-note">
                {language === 'es'
                  ? 'El código puede llegar a tu casilla de spam. Revisala y marcá a WannaColive como remitente seguro.'
                  : 'The code may arrive in your spam folder. Check it and mark WannaColive as a safe sender.'}
              </p>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={backToLogin}
                  className="text-sm text-muted-foreground hover:text-foreground underline inline-flex items-center gap-1"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-3 h-3" />
                  {language === 'es' ? 'Volver al login' : 'Back to login'}
                </button>
              </div>
            </>
          )}

          {view === 'reset' && (
            <>
              <div className="text-center mb-8">
                <h1 className="font-heading font-bold text-2xl text-foreground mb-2">
                  {language === 'es' ? 'Reestablecer contraseña' : 'Reset password'}
                </h1>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {language === 'es'
                    ? 'Ingresá el código que te enviamos a tu email y tu nueva contraseña.'
                    : 'Enter the code we sent to your email and your new password.'}
                </p>
              </div>

              {forgotInfo && (
                <div className="mb-4 p-3 bg-primary/10 border-2 border-foreground rounded-2xl text-sm text-foreground text-center" data-testid="forgot-info">
                  {forgotInfo}
                </div>
              )}

              <p className="mb-4 text-center text-xs text-muted-foreground leading-relaxed px-2" data-testid="reset-spam-note">
                {language === 'es'
                  ? '¿No te llegó? Revisá tu casilla de spam y marcá a WannaColive como remitente seguro.'
                  : "Didn't receive it? Check your spam folder and mark WannaColive as a safe sender."}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-destructive/15 border-2 border-destructive rounded-2xl text-sm text-destructive text-center" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleReset} className="space-y-3">
                <input
                  type="text"
                  inputMode="numeric"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={inputBase}
                  placeholder={language === 'es' ? 'Código de 6 dígitos' : '6-digit code'}
                  required
                  autoComplete="one-time-code"
                  data-testid="reset-code-input"
                />
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={inputBase + ' pr-12'}
                    placeholder={language === 'es' ? 'Nueva contraseña' : 'New password'}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    data-testid="reset-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showNewPassword ? (language === 'es' ? 'Ocultar contraseña' : 'Hide password') : (language === 'es' ? 'Mostrar contraseña' : 'Show password')}
                    data-testid="reset-password-toggle"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={loading || !resetCode || !newPassword}
                  className={`w-full py-4 rounded-2xl font-semibold text-base transition-all disabled:cursor-not-allowed ${
                    isLight
                      ? 'bg-[#0F0F0F] text-white hover:bg-[#2A2A2A] disabled:bg-[#5A5A5A]'
                      : 'bg-white text-[#0F0F0F] hover:bg-gray-200 disabled:bg-[#5A5A5A]'
                  }`}
                  data-testid="reset-submit-button"
                >
                  {loading
                    ? (language === 'es' ? 'Procesando…' : 'Processing…')
                    : (language === 'es' ? 'Reestablecer contraseña' : 'Reset password')}
                </button>
              </form>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={backToLogin}
                  className="text-sm text-muted-foreground hover:text-foreground underline inline-flex items-center gap-1"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-3 h-3" />
                  {language === 'es' ? 'Volver al login' : 'Back to login'}
                </button>
              </div>
            </>
          )}

          {view === 'reset-done' && (
            <>
              <div className="text-center mb-6">
                <h1 className="font-heading font-bold text-2xl text-foreground mb-2">
                  {language === 'es' ? '¡Contraseña actualizada!' : 'Password updated!'}
                </h1>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {language === 'es'
                    ? 'Ya podés iniciar sesión con tu nueva contraseña.'
                    : 'You can now sign in with your new password.'}
                </p>
              </div>

              <button
                type="button"
                onClick={backToLogin}
                className={`w-full py-4 rounded-2xl font-semibold text-base transition-all ${
                  isLight
                    ? 'bg-[#0F0F0F] text-white hover:bg-[#2A2A2A]'
                    : 'bg-white text-[#0F0F0F] hover:bg-gray-200'
                }`}
                data-testid="reset-done-btn"
              >
                {language === 'es' ? 'Ir al login' : 'Go to login'}
              </button>
            </>
          )}
        </div>
      </div>

      <div
        className="px-6 text-center text-xs text-muted-foreground"
        style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      >
        <Link to="/terminos" className="underline hover:text-foreground" data-testid="login-terms-link">
          {t('legalTermsLink')}
        </Link>
        <span className="px-2">·</span>
        <Link to="/privacidad" className="underline hover:text-foreground" data-testid="login-privacy-link">
          {t('legalPrivacyLink')}
        </Link>
        <span className="px-2">·</span>
        <Link to="/eliminar-cuenta" className="underline hover:text-foreground" data-testid="login-delete-account-link">
          {t('legalDeleteLink')}
        </Link>
      </div>
    </div>
  );
}
